import { useMemo, useState } from 'react';
import { Box, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import PhishingIcon from '@mui/icons-material/Phishing';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import { catchChange, fmtPct, fmtPrice, growthTone, signed, trendTone, winTone } from '../logic';
import { toneColor } from './tone';
import { GrowthPill } from './BucketCard';

const ROW_HEIGHT = 60;
const VISIBLE_ROWS = 5;

const GRID = {
  display: 'grid',
  alignItems: 'center',
  columnGap: 1.5,
  gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', sm: 'minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1fr) 120px 72px' },
  gridTemplateAreas: { xs: '"sym move" "catch trend"', sm: '"sym catch move trend win"' },
};

/**
 * Live Screening's Fishing bucket: from the 2nd refresh on, every symbol whose live score rose above
 * its trend-table score and is positive - or, at +4 (can't rise further), whose price is above today's
 * open - with the price locked at the catch. The 1st refresh catches nothing. The catch price turns green while the live
 * price is above it and red once it falls below.
 */
export default function FishingCard({ fishing, rows }) {
  const theme = useTheme();
  const accent = theme.palette.info.main;
  const isDark = theme.palette.mode === 'dark';
  const [sort, setSort] = useState({ key: 'change', desc: true });

  // Only catches still in the screened universe.
  const data = useMemo(() => {
    const byToken = new Map(rows.map((r) => [String(r.token), r]));
    const list = fishing.flatMap((fish) => {
      const row = byToken.get(String(fish.token));
      return row ? [{ row, fish, change: catchChange(fish.catchPrice, row.close) }] : [];
    });
    const value = (f: FishRow) => (sort.key === 'change' ? f.change : sort.key === 'symbol' ? f.row.symbol : f.fish.caughtAt);
    return list.sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      if (va == null && vb == null) return a.row.symbol.localeCompare(b.row.symbol);
      if (va == null) return 1;
      if (vb == null) return -1;
      const cmp = typeof va === 'string' ? va.localeCompare(vb) : (va) - (vb);
      return (sort.desc ? -cmp : cmp) || a.row.symbol.localeCompare(b.row.symbol);
    });
  }, [fishing, rows, sort]);

  const changes = data.map((d) => d.change).filter((c) => c != null);
  const avg = changes.length ? changes.reduce((a, b) => a + b, 0) / changes.length : null;
  const up = changes.filter((c) => c > 0).length;
  const down = changes.filter((c) => c < 0).length;

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, desc: !s.desc } : { key, desc: key !== 'symbol' }));

  return (
    <Paper
      id="bucket-fishing"
      sx={{
        overflow: 'hidden',
        scrollMarginTop: 96,
        borderTop: `3px solid ${accent}`,
        backgroundImage: `linear-gradient(180deg, ${alpha(accent, isDark ? 0.1 : 0.06)} 0%, transparent 140px)`,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, pt: 1.75, pb: 1.25 }}>
        <Box
          sx={{
            width: 44, height: 44, borderRadius: 2.5, display: 'grid', placeItems: 'center',
            color: theme.palette.getContrastText(accent), bgcolor: alpha(accent, 0.85),
            boxShadow: `0 6px 16px ${alpha(accent, 0.28)}`,
          }}
        >
          <PhishingIcon />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>Fishing</Typography>
          <Typography variant="caption" color="text.secondary">
            {data.length} caught · from the 2nd refresh: live score rose above the trend table and is positive (+4: price above open) · price locked at the catch
          </Typography>
        </Box>
      </Stack>

      <Box
        sx={{
          ...GRID, gridTemplateAreas: { xs: '"sym move"', sm: GRID.gridTemplateAreas.sm },
          px: 2, py: 0.75, borderTop: 1, borderBottom: 1, borderColor: 'divider',
          bgcolor: alpha(theme.palette.text.primary, isDark ? 0.04 : 0.025),
        }}
      >
        <HeaderCell area="sym" label="Symbol" sortKey="symbol" sort={sort} onSort={toggleSort} />
        <HeaderCell area="catch" label="Catch price" sortKey="caughtAt" sort={sort} onSort={toggleSort} hideXs />
        <HeaderCell area="move" label="Live · since catch" sortKey="change" sort={sort} onSort={toggleSort} />
        <Typography sx={{ gridArea: 'trend', display: { xs: 'none', sm: 'block' }, fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: 'text.secondary' }}>
          Caught · Now
        </Typography>
        <Typography sx={{ gridArea: 'win', display: { xs: 'none', sm: 'block' }, justifySelf: 'end', fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: 'text.secondary' }}>
          Win %
        </Typography>
      </Box>

      {data.length === 0 ? (
        <Box sx={{ height: ROW_HEIGHT * 1.5, display: 'grid', placeItems: 'center', px: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Nothing caught yet - from the 2nd refresh, a symbol lands here when its live score rises above the trend table and is positive, or is +4 with its price above today's open.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            maxHeight: { xs: (ROW_HEIGHT + 22) * VISIBLE_ROWS, sm: ROW_HEIGHT * VISIBLE_ROWS },
            overflowY: 'auto',
            scrollbarWidth: 'thin',
            scrollbarColor: `${alpha(accent, 0.45)} transparent`,
          }}
        >
          {data.map((d, idx) => <Row key={d.fish.token} data={d} striped={idx % 2 === 1} />)}
        </Box>
      )}

      <Stack
        direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" rowGap={0.5}
        sx={{ px: 2, py: 1.1, borderTop: 1, borderColor: 'divider', bgcolor: alpha(accent, isDark ? 0.08 : 0.05) }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          Catch → Live · avg change
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 700 }}>▲ {up}</Typography>
          <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 700 }}>▼ {down}</Typography>
          <GrowthPill value={avg} />
        </Stack>
      </Stack>
    </Paper>
  );
}

function Row({ data: { row, fish, change }, striped }) {
  const theme = useTheme();
  // Catch price colour: green while the live price is above it, red once it falls below.
  const cColor = toneColor(theme, growthTone(change));
  const wColor = toneColor(theme, winTone(row.winRate));
  const caught = new Date(fish.caughtAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <Box
      sx={{
        ...GRID,
        minHeight: ROW_HEIGHT, px: 2, py: { xs: 1, sm: 0 }, rowGap: 0.5,
        bgcolor: striped ? alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.025 : 0.018) : 'transparent',
        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
      }}
    >
      <Box sx={{ gridArea: 'sym', minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, letterSpacing: 0.2 }} noWrap>{row.symbol}</Typography>
        <Tooltip title={[row.symbolName, row.exchange].filter(Boolean).join(' · ')} placement="top-start">
          <Typography variant="caption" color="text.secondary" noWrap component="div">{row.symbolName ?? '—'}</Typography>
        </Tooltip>
      </Box>

      <Box sx={{ gridArea: 'catch', minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 800, color: cColor, fontVariantNumeric: 'tabular-nums' }}>
          {fmtPrice(fish.catchPrice)}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>caught {caught}</Typography>
      </Box>

      <Box sx={{ gridArea: 'move', minWidth: 0, justifySelf: { xs: 'end', sm: 'start' } }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtPrice(row.close)}</Typography>
          <Box
            component="span"
            sx={{ px: 0.6, py: 0.1, borderRadius: 1, fontSize: 11, fontWeight: 700, color: cColor, bgcolor: alpha(cColor, 0.12), fontVariantNumeric: 'tabular-nums' }}
          >
            {fmtPct(change)}
          </Box>
        </Stack>
        <Typography variant="caption" color="text.secondary" noWrap component="div" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 11 }}>
          H {fmtPrice(row.high)} · L {fmtPrice(row.low)}
        </Typography>
      </Box>

      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ gridArea: 'trend' }}>
        <Tooltip title={`Caught when the score moved ${signed(fish.fromTrend)} → ${signed(fish.toTrend)}`}>
          <Stack direction="row" alignItems="center" spacing={0.25}>
            <ScoreChip value={fish.fromTrend} muted />
            <NorthEastIcon sx={{ fontSize: 14, color: 'success.main' }} />
            <ScoreChip value={fish.toTrend} muted />
          </Stack>
        </Tooltip>
        <Typography variant="caption" color="text.disabled">·</Typography>
        <Tooltip title="Live score now">
          <Box component="span"><ScoreChip value={row.trend} /></Box>
        </Tooltip>
      </Stack>

      <Typography
        variant="body2"
        sx={{ gridArea: 'win', display: { xs: 'none', sm: 'block' }, justifySelf: 'end', fontWeight: 800, color: wColor, fontVariantNumeric: 'tabular-nums' }}
      >
        {row.winRate == null ? '—' : `${row.winRate.toFixed(1)}%`}
      </Typography>
    </Box>
  );
}

function ScoreChip({ value, muted = false }) {
  const theme = useTheme();
  const color = toneColor(theme, trendTone(value));
  return (
    <Box
      component="span"
      sx={{
        minWidth: 26, height: 22, px: 0.5, borderRadius: 1.25, display: 'inline-grid', placeItems: 'center',
        fontSize: 12, fontWeight: 800, fontVariantNumeric: 'tabular-nums',
        color: value == null ? 'text.disabled' : color,
        bgcolor: value == null ? 'transparent' : alpha(color, muted ? 0.08 : 0.16),
        border: value == null ? `1px dashed ${theme.palette.divider}` : `1px solid ${alpha(color, muted ? 0.2 : 0.35)}`,
      }}
    >
      {value == null ? '—' : signed(value)}
    </Box>
  );
}

function HeaderCell({ area, label, sortKey, sort, onSort, hideXs = false }) {
  const active = sort.key === sortKey;
  return (
    <Box
      component="button"
      type="button"
      onClick={() => onSort(sortKey)}
      sx={{
        gridArea: area, display: hideXs ? { xs: 'none', sm: 'inline-flex' } : 'inline-flex',
        justifySelf: 'start', alignItems: 'center', gap: 0.25,
        p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer', font: 'inherit',
        fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase',
        color: active ? 'text.primary' : 'text.secondary',
        '&:hover': { color: 'text.primary' },
      }}
    >
      {label}
      {active && (sort.desc ? <ArrowDownwardIcon sx={{ fontSize: 13 }} /> : <ArrowUpwardIcon sx={{ fontSize: 13 }} />)}
    </Box>
  );
}
